import { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { ATTEMPT_STATUSES } from "@miplatform/constants";
import { getTimeIntervals } from "@miplatform/utils";
import { Box } from "@mui/material";
import { CurrentTimeIndicator } from "./components/CurrentTimeIndicator";
import { PrioritySchedulerColumn } from "./components/PrioritySchedulerColumn";
import { PrioritySchedulerColumnHeader } from "./components/PrioritySchedulerColumnHeader";
import { Bucket } from "../MiScheduler/Bucket/Bucket";
import { TimeGutter } from "../MiScheduler/TimeGutter/TimeGutter";
import { MiWorkOrderCard } from "../MiWorkOrderCard/MiWorkOrderCard";
import { IPrioritySchedulerProps } from "./PrioritySchedulerBoard.types";

const HEIGHT_OF_HEADER = 28;

export function PrioritySchedulerBoard({
  columns = [],
  columnWidth,
  hourHeight,
  date,
  zoom = 1,
  calendarSet,
  onEventRemove,
  handleViewMore,
  trays,
  onCardClick = (_) => null,
  EventComponent = MiWorkOrderCard,
}: Readonly<IPrioritySchedulerProps>) {
  const schedulerColumnsRef = useRef<HTMLDivElement>(null);
  const [indicatorWidth, setIndicatorWidth] = useState<number>();

  const { dayStartTime } = calendarSet ?? {};

  const calculatedHourHeight = useMemo(
    () => hourHeight * zoom,
    [zoom, hourHeight]
  );

  const intervals = useMemo(() => getTimeIntervals(60), [60]);

  const totalContentHeight = useMemo(
    () => intervals.length * calculatedHourHeight,
    [intervals.length, calculatedHourHeight]
  );

  const dayStartTopPosition = useMemo(() => {
    if (!dayStartTime) return 0;
    const [hours = 0, minutes = 0] = dayStartTime.split(":").map(Number);
    const hoursToDayStartTime = (hours ?? 0) + (minutes ?? 0) / 60;
    return hoursToDayStartTime * calculatedHourHeight - HEIGHT_OF_HEADER;
  }, [dayStartTime, calculatedHourHeight]);

  const dayStartMarkerRef = useRef<HTMLDivElement>(null);

  const { classes, cx } = useStyles({ dayStartTopPosition });

  const handleHeaderAction = (actionType: string, userId?: string) => {
    if (actionType === "viewMore" && userId) {
      const user = columns
        .map((col) => col.user)
        .find((user) => user?.id === userId);
      if (user) {
        handleViewMore?.(user);
      }
    }
  };

  useEffect(() => {
    const schedulerColumnsElement = schedulerColumnsRef.current;
    if (!schedulerColumnsElement) {
      return;
    }

    setIndicatorWidth(schedulerColumnsElement.scrollWidth);
  }, [columns.length, columnWidth, calculatedHourHeight]);

  useEffect(() => {
    if (!dayStartTime) return;
    dayStartMarkerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [dayStartTopPosition]);

  return (
    <Box className={classes.prioritySchedulerContainer}>
      <Box className={classes.priorityScheduler}>
        <Box
          ref={schedulerColumnsRef}
          className={classes.prioritySchedulerColumns}
          height={totalContentHeight}
        >
          <Box className={classes.currentTimeIndicatorContainer}>
            <Box className={classes.currentTimeIndicatorContainerInner}>
              <Box className={classes.stickyHeader} />
              <Box className={classes.currentTimeIndicatorWrapper}>
                <CurrentTimeIndicator
                  hourHeight={calculatedHourHeight}
                  lineWidth={indicatorWidth}
                />
              </Box>
            </Box>
          </Box>
          <Box className={classes.stickyGutter}>
            <Box className={classes.stickyHeader} />
            <TimeGutter
              intervals={intervals}
              adjustedHeight={calculatedHourHeight}
            />
          </Box>
          <Box
            ref={dayStartMarkerRef}
            className={classes.dayStartMarker}
            data-testid="day-start-marker"
          />
          {columns.map((column) => {
            const columnDate = column.date ?? date;
            const { events = [] } = column;
            const holdCount = events.filter(
              (event) => event.attemptStatus === ATTEMPT_STATUSES.HOLD
            ).length;
            const eventCount = events.length;

            return (
              <Box key={column?.id} width={columnWidth} flex="0 0 auto">
                <Box
                  className={cx(classes.stickyHeader, classes.stickyHeaderBlue)}
                >
                  <PrioritySchedulerColumnHeader
                    {...column}
                    date={columnDate}
                    user={column.user}
                    holdCount={holdCount}
                    eventCount={eventCount}
                    dateView={column?.dateView}
                    handleHeaderAction={handleHeaderAction}
                  />
                </Box>
                <PrioritySchedulerColumn
                  {...column}
                  date={columnDate}
                  EventComponent={EventComponent}
                  hourHeight={calculatedHourHeight}
                  dayStartTime={dayStartTime}
                  onCardClick={onCardClick}
                  onEventRemove={onEventRemove}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box className={classes.bucketContainer}>
        {trays?.map((bucket) => (
          <Bucket
            key={bucket.id}
            bucket={{
              data: bucket.data,
              ...bucket,
            }}
          >
            {bucket.data?.map((event) => (
              <Box key={event.id} flexShrink={0}>
                <MiWorkOrderCard attempt={event} />
              </Box>
            ))}
          </Bucket>
        ))}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles<{ dayStartTopPosition: number }>()(
  (theme, { dayStartTopPosition }) => ({
    prioritySchedulerContainer: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
    },
    priorityScheduler: {
      display: "flex",
      paddingTop: theme.spacing(1),
      minWidth: 0,
    },
    prioritySchedulerColumns: {
      display: "flex",
      backgroundColor: theme.palette.common.white,
      gap: theme.spacing(1),
      position: "relative",
      minWidth: 0,
      width: "max-content",
    },
    currentTimeIndicatorWrapper: {
      height: "100%",
      width: "100%",
      position: "relative",
    },
    currentTimeIndicatorContainer: {
      width: "100%",
      position: "absolute",
      zIndex: 3,
      height: "100%",
      top: 0,
      left: 0,
      right: 0,
      pointerEvents: "none",
    },
    currentTimeIndicatorContainerInner: {
      position: "relative",
      width: "100%",
      height: "100%",
    },
    stickyGutter: {
      position: "sticky",
      left: 0,
      zIndex: 2,
    },
    stickyHeader: {
      position: "sticky",
      // This is fixing the issue of the sticky header being cut off when the page is scrolled
      top: -4,
      padding: theme.spacing(0, 1),
      height: 28,
      zIndex: 3,
    },
    stickyHeaderBlue: {
      backgroundColor: theme.palette.blue.light,
      padding: 4,
      borderTopRightRadius: theme.spacing(1),
      borderTopLeftRadius: theme.spacing(1),
    },
    bucketContainer: {
      display: "flex",
      flexDirection: "column",
      position: "sticky",
      bottom: 0,
      left: 0,
      backgroundColor: theme.palette.background.paper,
    },
    dayStartMarker: {
      position: "absolute",
      left: 0,
      top: dayStartTopPosition,
      width: 1,
      height: 1,
      pointerEvents: "none",
    },
  })
);
